import axios, { type AxiosResponse } from 'axios';

import { getConfig } from '@/features/environment/config';

import { createApiInstance } from '@/shared/authentication';

import type {
  LrpAnalysisFilterSettings,
  LrpPriceEvolutionAnalysisSettings
} from '@/types/LrpAnalysis';

import InsurancePlan from '../types/dto/InsurancePlan';
import { formatDateISO } from '../utils/dateFormatter';

import type { TagTypeRequestBody } from '../containers/AdminPanelContainers/TagTypes/types/TagType';
import type { InsuranceAgentPayload } from '../ducks/adminPanel/actions';
import type { ProjectionDataQuery } from '../ducks/projectionData/actions';
import type { TagRequestBody } from '../scenes/common/Tags/types/Tag';
import type {
  LrpDeleteEndorsement,
  LrpEndorsementSummaryDto,
  LrpUpdateEndorsement
} from '../scenes/lrp/types/LrpEndorsement';
import type {
  LrpCalculationParameters,
  LrpCoverageRequestModel,
  LrpCoverageRequestUpdateModel,
  LrpEndorsementDefinition
} from '../scenes/lrp/types/LrpGetFilters';
import type PricePreferencePayload from '../scenes/lrp/types/PricePreferencePayload';
import type { AccountPreferencesPassword } from '../types/AccountPreferences/AccountPreferencesPassword';
import type { AccountPreferencesSendReport } from '../types/AccountPreferences/AccountPreferencesSendReport';
import type { AccountPreferencesUpdatePayload } from '../types/AccountPreferences/AccountPreferencesUpdatePayload';
import type { Agency } from '../types/Agency';
import type { AlertMeasureType, AlertPriceType } from '../types/Alerts';
import type { AnalysisProduct } from '../types/AnalysisProduct';
import type { AccountPreferencesDto } from '../types/dto/AccountPreferencesDto';
import type AgentDto from '../types/dto/AgentDto';
import type {
  AipDrpEndorsement,
  AipDtoMatching,
  AipLrpEndorsement
} from '../types/dto/AipOperationDto';
import type {
  BindingRequestDto,
  CoverageRequestDto,
  CoverageRequestEndorsementDefinition,
  DrpCoverageRequestUpdateModel
} from '../types/dto/CoverageRequest';
import type { GroupStatisticsAllExcel } from '../types/dto/DrpStatisticsDto';
import type {
  DrpEndorsementDto,
  EndorsementDrp,
  EndorsementLrp
} from '../types/dto/EndorsementDto';
import type { EndorsementSummaryDto } from '../types/dto/EndorsementSummaryDto';
import type { HistoricalAnalysisActualDto } from '../types/dto/HistoricalAnalysisActualDto';
import type { HistoricalAnalysisDto } from '../types/dto/HistoricalAnalysisDto';
import type {
  HistoricalAnalysisExpectedDto,
  HistoricalAnalysisPriceFloorDto
} from '../types/dto/HistoricalAnalysisExpectedDto';
import type { HistoricalDataDto } from '../types/dto/HistoricalDataDto';
import type { OperationContactDto } from '../types/dto/OperationContactDto';
import type { OperationDto } from '../types/dto/OperationDto';
import type {
  OperationPreferencesDto,
  OperationPriceReportPayload
} from '../types/dto/OperationPreferencesDto';
import type { PracticeDto } from '../types/dto/PracticeDto';
import type { ProducerDto } from '../types/dto/ProducerDto';
import type { ProjectionAnalysisDataDto } from '../types/dto/ProjectionAnalysisDataDto';
import type { ProjectionAnalysisParametersDto } from '../types/dto/ProjectionAnalysisParametersDto';
import type { ScenarioDto } from '../types/dto/ScenarioDto';
import type CreateEducationVideoData from '../types/EducationModule/CreateEducationVideoData';
import type EducationVideo from '../types/EducationModule/EducationVideo';
import type {
  EndorsementPayload,
  EndorsementPayloadBinding
} from '../types/Endorsement/EndorsementPayload';
import type { HistoricalDataEssentials } from '../types/HistoricalDataEssentials';
import type { OperationReport } from '../types/ManagementReports/OperationReport';
import type { Operation } from '../types/Operation/Operation';
import type { ProducerAddData, ProducerEditData } from '../types/Producer/ProducerModalData';
import type { ProducerPreferencesUpdateData } from '../types/Producer/ProducerPreferencesUpdateData';
import type { Quarter } from '../types/Quarter';
import type { Scenario } from '../types/Scenario';
import type { ScenarioAnalysisWhatIfPost } from '../types/ScenarioWhatIf/ScenarioAnalysisWhatIfPayload';
import type { ScenarioWhatIfInitialPrices } from '../types/ScenarioWhatIf/ScenarioWhatIfPrices';
import type { AgentLicense } from '../types/shared/AgentLicense';
import type { ProducerType } from '../types/UserType';
import type { WhatIfBody } from '../types/WhatIfBody';

export const api = createApiInstance({
  baseURL: `${getConfig()?.INSURANCE_URL ?? ''}`,
  timeout: 120000
});

export const DRPPriceReportAPI = createApiInstance({
  baseURL: `${getConfig()?.INSURANCE_URL ?? ''}`,
  timeout: 120000
});

export const addQueryParameter = (
  currentParams: string,
  query: string,
  param: string | number | boolean // | number[] | string[]
): string => {
  const params = currentParams ? `${currentParams}&` : '';
  const queryString = `${params}${query}=${param}`;
  return queryString;
};

export const addQueryParameterArray = (
  currentParams: string,
  query: string,
  params: string[]
): string => {
  if (!params?.length) {
    return currentParams;
  }
  return params.reduce((result, param) => addQueryParameter(result, query, param), currentParams);
};

interface InsuranceService {
  operationName: string;
  stateCode: number;
  practiceCode: number;
  salesEffectiveDate: Date;
  butterfatTest: number;
  proteinTest: number;
  classPriceWeightingFactor: number;
  componentPriceWeightingFactor: number;
  coverageLevel: number;
  coveredMilkProduction: number;
  protectionFactor: number;
  share: number;
}

const insuranceAPI = {
  getInsurance: async (
    {
      operationName,
      stateCode,
      practiceCode,
      salesEffectiveDate,
      componentPriceWeightingFactor, // query
      butterfatTest,
      proteinTest,
      classPriceWeightingFactor,
      coverageLevel,
      coveredMilkProduction,
      protectionFactor,
      share
    }: InsuranceService,
    reinsuranceYear: number
  ) => {
    const salesDate = formatDateISO(salesEffectiveDate);
    const state = String(stateCode).padStart(2, '0');

    let query = '';
    query = addQueryParameter(query, 'ButterfatTest', butterfatTest);
    query = addQueryParameter(query, 'ProteinTest', proteinTest);
    query = addQueryParameter(query, 'ClassPriceWeightingFactor', classPriceWeightingFactor);
    if (reinsuranceYear > 2020) {
      query = addQueryParameter(
        query,
        'ComponentPriceWeightingFactor',
        componentPriceWeightingFactor
      );
    }
    query = addQueryParameter(query, 'CoverageLevel', coverageLevel);
    query = addQueryParameter(query, 'CoveredMilkProduction', coveredMilkProduction);
    query = addQueryParameter(query, 'ProtectionFactor', protectionFactor);
    query = addQueryParameter(query, 'Share', share);
    return await api.get(
      `drp/operation/${operationName}/scenario/simulate/${state}/${practiceCode}/${salesDate}${
        query ? `?${query}` : ''
      }`
    );
  },

  updateWhatIf: async (
    operationName: string,
    stateCode: string,
    practiceCode: string,
    salesEffectiveDate: string,
    data: WhatIfBody
  ) =>
    await api.post(
      `/drp/operation/${operationName}/scenario/simulate/${stateCode}/${practiceCode}/${salesEffectiveDate}/whatif`,
      data
    ),

  updateIndemnity: async (
    operationName: string,
    stateCode: string,
    practiceCode: string,
    salesEffectiveDate: string,
    data: WhatIfBody
  ) =>
    await api.post(
      `/drp/operation/${operationName}/scenario/simulate/${stateCode}/${practiceCode}/${salesEffectiveDate}/indemnity`,
      data
    ),

  getInsurancePractice: async (
    operationName: string,
    stateCode: string
  ): Promise<AxiosResponse<PracticeDto>> =>
    await api.get(`operation/${operationName}/scenario/practice/${stateCode}`),

  getInsurancePracticeSales: async (
    operationName: string,
    stateCode: string,
    salesDate: Date
  ): Promise<AxiosResponse<PracticeDto>> =>
    await api.get(
      `drp/operation/${operationName}/scenario/practice/${stateCode}/${formatDateISO(salesDate)}`
    ),

  getStates: async () => await api.get(`/udm/states?year=${new Date().getFullYear()}`),

  getCounties: async (stateCode?: string) =>
    await api.get(stateCode ? `/county?stateCode=${stateCode}` : '/county'),

  // Agencies
  getAgencies: async (): Promise<AxiosResponse<Agency[]>> => await api.get('agency'),

  createAgency: async (data: Agency) => await api.post('agency', data),

  editAgency: async (agencyName: string, data: Agency) =>
    await api.put(`agency/${agencyName}`, data),

  deleteAgency: async (agencyName: string) => await api.delete(`agency/${agencyName}`),

  // Operations
  getAgencyOperations: async (
    agencyName: string,
    includeBindingRequirements?: boolean
  ): Promise<AxiosResponse<OperationDto[]>> =>
    await api.get(
      `agency/${agencyName}/operation${
        includeBindingRequirements ? '?includeBindingRequirements=true' : ''
      }`
    ),

  getAgentOperations: async (
    agent: string,
    includeBindingRequirements?: boolean
  ): Promise<AxiosResponse<OperationDto[]>> =>
    await api.get(
      `/agent/${agent}/operation${
        includeBindingRequirements ? '?includeBindingRequirements=true' : ''
      }`
    ),

  getAgencyOperation: async (
    agencyName: string,
    operationName: string,
    includeBindingRequirements?: boolean
  ): Promise<AxiosResponse<OperationDto>> =>
    await api.get(
      `/agency/${agencyName}/operation/${operationName}${
        includeBindingRequirements ? '?includeBindingRequirements=true' : ''
      }`
    ),

  editAgencyOperation: async (
    agencyName: string,
    operationName: string,
    data: Operation & { primaryContact?: string }
  ) => await api.put(`agency/${agencyName}/operation/${operationName}`, data),

  createAgencyOperation: async (agencyName: string, data: Omit<Operation, 'name'>) =>
    await api.post(`agency/${agencyName}/operation`, data),

  deleteAgencyOperation: async (agencyName: string, operationName: string) =>
    await api.delete(`/agency/${agencyName}/operation/${operationName}`),

  // Agents
  getAgencyAgents: async (
    agencyName: string,
    includeBindingRequirements?: boolean
  ): Promise<AxiosResponse<AgentDto[]>> =>
    await api.get(
      `agency/${agencyName}/agent${
        includeBindingRequirements ? '?includeBindingRequirements=true' : ''
      }`
    ),

  getAgencyAgent: async (
    agencyName: string,
    agentName: string,
    includeBindingRequirements?: boolean
  ): Promise<AxiosResponse<AgentDto>> =>
    await api.get(
      `agency/${agencyName}/agent/${agentName}${
        includeBindingRequirements ? '?includeBindingRequirements=true' : ''
      }`
    ),

  createAgencyAgent: async (agencyName: string, data: InsuranceAgentPayload) =>
    await api.post(`/agency/${agencyName}/agent`, data),

  deleteAgencyAgent: async (agencyName: string, userName: string) =>
    await api.delete(`/agency/${agencyName}/agent/${userName}`),

  updateAgencyAgent: (
    agencyName: string,
    agentName: string,
    data: {
      firstName: string;
      lastName: string;
      email: string;
    }
  ) => api.put(`/agency/${agencyName}/agent/${agentName}`, data),

  getAgentLicenses: async (
    agencyName: string,
    agentName: string
  ): Promise<AxiosResponse<AgentLicense[]>> =>
    await api.get(`agency/${agencyName}/agent/${agentName}/licence`),

  createAgentLicense: async (agencyName: string, agentName: string, data: AgentLicense) =>
    await api.post(`/agency/${agencyName}/agent/${agentName}/licence`, data),

  deleteAgentLicense: async (agencyName: string, agentName: string, stateCode: string) =>
    await api.delete(`/agency/${agencyName}/agent/${agentName}/licence/${stateCode}`),

  resendAgentInvitation: (agencyName: string, agentName: string) =>
    api.post(`/agency/${agencyName}/agent/${encodeURIComponent(agentName)}/verification/resend`),

  // AIPSync
  getAipStatus: async (agencyName: string) => await api.get(`aip/${agencyName}/status`),

  getAipOperations: async (data: {
    agencyName: string;
    insurancePlan?: string;
    includeProbableMatches?: boolean;
    reinsuranceYear?: number;
  }) => {
    const insurancePlanPath = data.insurancePlan ? `/${data.insurancePlan}` : '';
    const includeProbableMatchesQuery = `includeProbableMatches=${
      data.includeProbableMatches ?? false
    }`;
    const reinsuranceYearQuery = data.reinsuranceYear
      ? `&reinsuranceYear=${data.reinsuranceYear}`
      : '';
    const url = `aip/${data.agencyName}/operation${insurancePlanPath}?${includeProbableMatchesQuery}${reinsuranceYearQuery}`;

    return await api.get(url);
  },

  getAipEndorsements: async (agencyName: string, operationName: string, insurancePlan: string) =>
    await api.get(`aip/${agencyName}/operation/${operationName}/${insurancePlan}`),

  matchAipOperation: async (agencyName: string, operationName: string, data: AipDtoMatching) =>
    await api.put(`aip/${agencyName}/operation/${operationName}`, data),

  unmatchOperation: async (
    operationToUnMatch: AipDtoMatching,
    data: {
      agencyName: string;
      insurancePlan?: string | undefined;
    }
  ) => await api.delete(`aip/${data.agencyName}/operation`, { data: operationToUnMatch }),

  importAipEndorsement: async (
    endorsementToUnmatch: AipDrpEndorsement | AipLrpEndorsement,
    agencyName: string,
    operationName: string,
    id: string,
    insurancePlan: string
  ) =>
    await api.put(
      `aip/${agencyName}/operation/${operationName}/${insurancePlan}/${id}`,
      endorsementToUnmatch
    ),

  ignoreAipDiscrepancy: async (
    operationName: string,
    agencyName: string,
    id: string,
    aipIgnore: boolean,
    insurancePlan: string
  ) =>
    await api.put(
      `aip/${agencyName}/operation/${operationName}/${insurancePlan}/${id}/AipIgnore?aipIgnore=${aipIgnore}`
    ),

  deleteAipEndorsement: async (
    endorsementToUnmatch: AipDrpEndorsement | AipLrpEndorsement,
    agencyName: string,
    operationName: string,
    insurancePlan: string
  ) =>
    await api.delete(`aip/${agencyName}/operation/${operationName}/${insurancePlan}`, {
      data: endorsementToUnmatch
    }),

  // Producers
  getAgencyProducers: async (
    agencyName: string,
    includeBindingRequirements = true,
    producerType?: ProducerType
  ): Promise<AxiosResponse<ProducerDto[]>> =>
    await api.get(`/agency/${agencyName}/producer`, {
      params: {
        includeBindingRequirements,
        producerType
      }
    }),

  deleteAgencyProducer: async (agencyName: string, producerName: string) =>
    await api.delete(`/agency/${agencyName}/producer/${producerName}`),

  updateAgencyProducer: async (agencyName: string, producerName: string, data: ProducerEditData) =>
    await api.put(`/agency/${agencyName}/producer/${producerName}`, data),

  resendProducerInvitation: (agencyName: string, producerName: string) =>
    api.post(
      `/agency/${agencyName}/producer/${encodeURIComponent(producerName)}/verification/resend`
    ),

  // Operation Producers
  getAgencyOperationProducers: async (
    agencyName: string,
    operationName: string,
    producerType?: ProducerType
  ): Promise<AxiosResponse<ProducerDto[]>> =>
    await api.get(`agency/${agencyName}/operation/${operationName}/producer`, {
      params: { producerType }
    }),

  createAgencyOperationProducer: async (
    agencyName: string,
    operationName: string,
    data: ProducerAddData
  ) => await api.post(`agency/${agencyName}/operation/${operationName}/producer`, data),

  removeAgencyOperationProducer: async (
    agencyName: string,
    operationName: string,
    username: string
  ) => await api.delete(`agency/${agencyName}/operation/${operationName}/producer/${username}`),

  getProducerOperations: async (
    producer: string,
    includeBindingRequirements?: boolean
  ): Promise<AxiosResponse<OperationDto[]>> =>
    await api.get(
      `/producer/${producer}/operation${
        includeBindingRequirements ? '?includeBindingRequirements=true' : ''
      }`
    ),

  getProducerOperation: async (
    producer: string,
    operationName: string,
    includeBindingRequirements?: boolean
  ): Promise<AxiosResponse<OperationDto>> =>
    await api.get(
      `/producer/${producer}/operation/${operationName}${
        includeBindingRequirements ? '?includeBindingRequirements=true' : ''
      }`
    ),

  setProducerFavoriteOperation: async (producer: string, operationName: string, stared: boolean) =>
    await api.put(`/producer/${producer}/producer/${operationName}?starred=${stared}`),

  // Account Preference
  getAccountPreferences: async (): Promise<AxiosResponse<AccountPreferencesDto>> =>
    await api.get('/account/preferences'),

  updateAccountPreferences: async (account: AccountPreferencesUpdatePayload) =>
    await api.put('/account/preferences', account),

  updateAccountStarredPreferences: async (showStarredOperationsOnly: boolean) =>
    await api.put(
      `/account/preferences/star?showStarredOperationsOnly=${showStarredOperationsOnly}`
    ),

  updateAccountPreferencesAndPassword: async (account: AccountPreferencesPassword) =>
    await api.put('/account/preferences', account),

  getSpecifiedAccountPreferences: async (
    userName: string
  ): Promise<AxiosResponse<AccountPreferencesDto>> =>
    await api.get(`/account/${userName}/preferences`),

  updateSpecifiedAccountPreferences: async (
    userName: string,
    preferences: ProducerPreferencesUpdateData
  ): Promise<AxiosResponse<AccountPreferencesDto>> =>
    await api.put(`/account/${userName}/preferences`, preferences),

  // Scenario
  getScenario: async (operationName: string): Promise<AxiosResponse<string[]>> =>
    await api.get(`/drp/operation/${operationName}/scenario`),

  getScenarioByName: async (
    name: string,
    operationName: string
  ): Promise<AxiosResponse<ScenarioDto[]>> =>
    await api.get(`/drp/operation/${operationName}/scenario/name?name=${encodeURIComponent(name)}`),

  // TODO: change Scenario to ScenarioDto
  putScenario: async (operationName: string, name: string, data: Scenario) =>
    await api.put(
      `/drp/operation/${operationName}/scenario?name=${encodeURIComponent(name)}`,
      data
    ),

  deleteScenario: async (name: string, operationName: string) =>
    await api.delete(`/drp/operation/${operationName}/scenario?name=${encodeURIComponent(name)}`),

  // Endorsements
  getEndorsements: async (operationName: string): Promise<AxiosResponse<DrpEndorsementDto[]>> =>
    await api.get(`drp/${operationName}/endorsement`),

  getCoverageRequests: async (
    operationName?: string,
    insurancePlan?: InsurancePlan
  ): Promise<AxiosResponse<CoverageRequestDto[]>> => {
    if (!operationName) {
      return await api.get('/coverage/request');
    }

    const insurancePlanParameter = insurancePlan
      ? `?insurancePlan=${InsurancePlan[insurancePlan]}`
      : '';
    return await api.get(`operation/${operationName}/coverage/request${insurancePlanParameter}`);
  },

  getCoverageRequest: async (
    operationName: string,
    referenceIdentifier: string
  ): Promise<AxiosResponse<CoverageRequestDto>> =>
    await api.get(`/operation/${operationName}/coverage/request/${referenceIdentifier}`),

  updateCoverageRequest: async (
    operationName: string,
    referenceIdentifier: string,
    data: { status: string } | LrpCoverageRequestUpdateModel | DrpCoverageRequestUpdateModel,
    insurancePlan: InsurancePlan
  ) => {
    if ('definitions' in data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- refactor later
      const endorsementDefinitions: any = [];
      data.definitions.forEach(
        (
          endorsementDefinition:
            | CoverageRequestEndorsementDefinition<LrpEndorsementDefinition>
            | CoverageRequestEndorsementDefinition<EndorsementPayload>
        ) => {
          if ('calculationParameters' in endorsementDefinition.definition) {
            endorsementDefinitions.push({
              id: endorsementDefinition.id,
              definition: {
                ...endorsementDefinition.definition,
                stateCode: endorsementDefinition.definition.stateCode.padStart(2, '0'),
                calculationParameters: {
                  ...endorsementDefinition.definition.calculationParameters,
                  salesEffectiveDate: formatDateISO(
                    endorsementDefinition.definition.calculationParameters.salesEffectiveDate
                  )
                },
                tags: endorsementDefinition.definition.tags
              }
            });
          } else if ('parameters' in endorsementDefinition.definition) {
            endorsementDefinitions.push({
              id: endorsementDefinition.id,
              definition: {
                ...endorsementDefinition.definition,
                stateCode: endorsementDefinition.definition.stateCode.padStart(2, '0'),
                calculationParameters: {
                  ...endorsementDefinition.definition.parameters
                },
                salesEffectiveDate: formatDateISO(
                  endorsementDefinition.definition.salesEffectiveDate
                ),
                tags: endorsementDefinition.definition.tags
              }
            });
          }
        }
      );
      data = {
        ...data,
        definitions: endorsementDefinitions
      };
    }

    await api.put(
      `${InsurancePlan[insurancePlan]}/${operationName}/endorsement/request/${referenceIdentifier}`,
      data
    );
  },

  getCoverageRequestHistory: async (
    operationName: string,
    referenceIdentifier: string
  ): Promise<AxiosResponse<BindingRequestDto[]>> =>
    await api.get(`operation/${operationName}/coverage/request/${referenceIdentifier}/history`),

  getEndorsementsByCoverageRequest: async (
    operationName: string,
    referenceIdentifier: string,
    insurancePlan: string
  ): Promise<AxiosResponse<EndorsementDrp[] | EndorsementLrp[]>> =>
    await api.get(`${insurancePlan}/${operationName}/endorsement/request/${referenceIdentifier}`),

  getEndorsementsWithFilter: async (
    operationName: string,
    reinsuranceYear: number,
    quarter: number,
    tagNames: string[]
  ): Promise<AxiosResponse<DrpEndorsementDto[]>> => {
    const query = addQueryParameterArray('', 'TagNames', tagNames);
    return await api.get(
      `drp/${operationName}/endorsement/calendar/${reinsuranceYear}/${quarter}${
        query ? `?${query}` : ''
      }`
    );
  },

  createDrpEndorsement: async (operationName: string, data: EndorsementPayload) =>
    await api.post(`drp/${operationName}/endorsement`, data),

  createEndorsementBinding: async (operationName: string, data: EndorsementPayloadBinding) =>
    await api.post(`drp/${operationName}/endorsement/request`, data),

  updateOperationBindingRequestStatus: async (
    operationName: string,
    referenceIdentifier: string,
    status: string,
    insurancePlan: InsurancePlan
  ) => {
    const config = { headers: { 'Content-Type': 'application/json' } };
    await api.put(
      `${InsurancePlan[insurancePlan]}/${operationName}/coverage/request/${referenceIdentifier}/status`,
      status,
      config
    );
  },

  downloadForm: async (
    operationName: string,
    referenceIdentifier: string,
    insurancePlan: InsurancePlan
  ) =>
    await api.get(
      `${InsurancePlan[insurancePlan]}/${operationName}/coverage/request/${referenceIdentifier}/form`,
      {
        responseType: 'blob'
      }
    ),

  updateEndorsement: async (operationName: string, id: number, data: EndorsementPayload) =>
    await api.put(`drp/${operationName}/endorsement/${id}`, data),

  deleteEndorsement: async (operationName: string, id: number) =>
    await api.delete(`drp/${operationName}/endorsement/${id}`),

  exportEndorsementIndemnityToExcel: async (operationName: string, id: number) =>
    await api.get(`/drp/${operationName}/endorsement/${id}/export`, { responseType: 'blob' }),

  getEndorsementSummary: async (
    operationName: string,
    tagNames: string[]
  ): Promise<AxiosResponse<EndorsementSummaryDto[]>> => {
    const query = addQueryParameterArray('', 'TagNames', tagNames);
    const baseUrl = `drp/operation/${operationName}/endorsement/summary`;
    const url = query ? `${baseUrl}?${query}` : baseUrl;
    return await api.get(url);
  },

  recalculateEndorsements: async (operationName: string) =>
    await api.put(`/drp/${operationName}/endorsement/price`),

  getEndorsementBasisCalculation: async (operationName: string, id: number) =>
    await api.get(`drp/${operationName}/endorsement/${id}/basis`),

  // historicalData aka OperationProduction
  getHistoricalData: async (operationName: string): Promise<AxiosResponse<HistoricalDataDto[]>> =>
    await api.get(`/operation/${operationName}/production`),

  downloadHistoricalData: async (operationName: string) =>
    await api.get(`/operation/${operationName}/production/download`),

  downloadProjectionTable: async (data: ProjectionAnalysisDataDto, projectionModel: number) =>
    await api.post(
      '/drp/analysis/projected/revenue/export/excel',
      { ...data, projectionModel },
      { responseType: 'blob' }
    ),

  editHistoricalRecord: async (operationName: string, data: HistoricalDataEssentials) =>
    await api.put(`/operation/${operationName}/production`, [data]),

  deleteHistoricalRecord: async (operationName: string, year: number, month: number) =>
    await api.delete(`/operation/${operationName}/production/${year}/${month}`),

  saveAllHistoricalRecords: async (operationName: string, data: HistoricalDataEssentials[]) =>
    await api.put(`/operation/${operationName}/production`, data),

  // operationContact
  getOperationAgentContacts: async (
    operationName: string
  ): Promise<AxiosResponse<OperationContactDto[]>> =>
    await api.get(`/operation/${operationName}/agent`),

  getAgentOperationContacts: async (
    agent: string,
    agencyName: string
  ): Promise<AxiosResponse<OperationContactDto[]>> =>
    await api.get(`/agency/${agencyName}/agent/${encodeURI(agent)}/operation/contact`),

  newAgentOperationContact: async (
    operationName: string,
    data: { agentName: string; notification: boolean; primaryContact: boolean }
  ) => await api.post(`/operation/${operationName}/agent`, data),

  updateAgentOperationContact: async (
    operationName: string,
    data: { agentName: string; notification: boolean; primaryContact: boolean }
  ) => await api.put(`/operation/${operationName}/agent`, data),

  deleteOperationAgentContact: async (agent: string, operationName: string) =>
    await api.delete(`/operation/${operationName}/agent/${agent}`),

  operationProductionUploadStatus: async (operationName: string, guid: string) =>
    await api.get(`operation/${operationName}/production/upload/${guid}`),

  getAgencyAgentOperationsContacts: async (
    agencyName: string
  ): Promise<AxiosResponse<OperationContactDto[]>> =>
    await api.get(`/agency/${agencyName}/operation/agent`),

  getAgencyProducerOperationsContacts: async (
    producerMail: string
  ): Promise<AxiosResponse<OperationContactDto[]>> =>
    await api.get(`/producer/${producerMail}/operation/agent`),

  setAgentFavoriteOperation: async (producer: string, operationName: string, stared: boolean) =>
    await api.put(`/agent/${producer}/${operationName}?starred=${stared}`),

  getAgencyLogo: async (): Promise<AxiosResponse<string>> => await api.get('/agency/logo'),

  getAdminAgencyLogo: async (agency: string): Promise<AxiosResponse<string>> =>
    await api.get(`/agency/${agency}/logo`),

  // Historical Analysis/Price Trends
  getHistoricalAnalysis: async (
    salesEffectiveDate: Date,
    yearFrom: number,
    yearTo: number,
    practiceCode: number,
    data: {
      calculationParameters: {
        butterfatTest: number;
        proteinTest: number;
        classPriceWeightingFactor: number;
        componentPriceWeightingFactor: number;
        coverageLevel: number;
        coveredMilkProduction: number;
        protectionFactor: number;
        share: number;
      };
      producerPremiumCwt: { class: 0; component: 0 };
    }
  ): Promise<AxiosResponse<HistoricalAnalysisDto[]>> => {
    const salesDate = formatDateISO(salesEffectiveDate);
    return await api.post(
      `/drp/analysis/historical?salesEffectiveDate=${salesDate}&yearFrom=${yearFrom}&yearTo=${yearTo}&practiceCode=${practiceCode}`,
      data
    );
  },

  getHistoricalAnalysisPriceFutures: async (
    year: number,
    quarter: number,
    product: AnalysisProduct,
    month: number
  ) =>
    await api.get<HistoricalAnalysisExpectedDto>(
      `/drp/analysis/price/futures?year=${year}&quarter=${quarter}&product=${product}&month=${month}`
    ),

  getHistoricalAnalysisExpectedFutures: async (
    year: number,
    quarter: number,
    product: AnalysisProduct
  ) =>
    await api.get<HistoricalAnalysisExpectedDto>(
      `/drp/analysis/historical/price/expected/futures?year=${year}&quarter=${quarter}&product=${product}`
    ),

  getHistoricalAnalysisActual: async (yearFrom: number, yearTo: number, product: AnalysisProduct) =>
    await api.get<HistoricalAnalysisActualDto>(
      `/drp/analysis/historical/price/actual?yearFrom=${yearFrom}&yearTo=${yearTo}&product=${product}`
    ),

  getHistoricalAnalysisExpectedRMA: async (
    year: number,
    quarter: number,
    product: AnalysisProduct
  ) =>
    await api.get<HistoricalAnalysisExpectedDto>(
      `/drp/analysis/historical/price/expected/rma?year=${year}&quarter=${quarter}&product=${product}`
    ),

  getScenarioAlertExpectedPrices: async (
    operationName: string,
    year: number,
    quarter: Quarter,
    priceType: AlertPriceType,
    measureType: AlertMeasureType,
    classPriceWeightingFactor: number,
    componentPriceWeightingFactor: number | null,
    coverageLevel: number,
    proteinTest: number,
    butterfatTest: number
  ) => {
    let query = '';
    query = addQueryParameter(query, 'year', year);
    query = addQueryParameter(query, 'quarter', quarter);
    query = addQueryParameter(query, 'priceType', priceType);
    query = addQueryParameter(query, 'measureType', measureType);
    query = addQueryParameter(query, 'classPriceWeightingFactor', classPriceWeightingFactor);
    if (componentPriceWeightingFactor !== null) {
      query = addQueryParameter(
        query,
        'componentPriceWeightingFactor',
        componentPriceWeightingFactor
      );
    }
    query = addQueryParameter(query, 'coverageLevel', coverageLevel);
    query = addQueryParameter(query, 'butterfatTest', butterfatTest);
    query = addQueryParameter(query, 'proteinTest', proteinTest);

    const baseUrl = `/drp/analysis/scenario/${operationName}`;
    const url = query ? `${baseUrl}?${query}` : baseUrl;
    return await api.get<HistoricalAnalysisExpectedDto>(url);
  },

  getScenarioAlertPriceFloor: async (
    operationName: string,
    year: number,
    quarter: Quarter,
    salesEffectiveDate: Date,
    priceType: AlertPriceType,
    measureType: AlertMeasureType,
    classPriceWeightingFactor: number,
    componentPriceWeightingFactor: number | null,
    coverageLevel: number,
    proteinTest: number,
    butterfatTest: number,
    protectionFactor?: number,
    basis?: number
  ) => {
    const salesDate = formatDateISO(salesEffectiveDate);
    let query = '';
    query = addQueryParameter(query, 'year', year);
    query = addQueryParameter(query, 'quarter', quarter);
    query = addQueryParameter(query, 'salesEffectiveDate', salesDate);
    query = addQueryParameter(query, 'priceType', priceType);
    query = addQueryParameter(query, 'measureType', measureType);
    query = addQueryParameter(query, 'classPriceWeightingFactor', classPriceWeightingFactor);
    if (componentPriceWeightingFactor !== null) {
      query = addQueryParameter(
        query,
        'componentPriceWeightingFactor',
        componentPriceWeightingFactor
      );
    }
    query = addQueryParameter(query, 'coverageLevel', coverageLevel);
    query = addQueryParameter(query, 'butterfatTest', butterfatTest);
    query = addQueryParameter(query, 'proteinTest', proteinTest);
    query = protectionFactor
      ? addQueryParameter(query, 'protectionFactor', protectionFactor)
      : query;
    query = basis ? addQueryParameter(query, 'basis', basis) : query;

    const baseUrl = `/drp/analysis/scenario/price/floor/${operationName}`;
    const url = query ? `${baseUrl}?${query}` : baseUrl;
    return await api.get<HistoricalAnalysisPriceFloorDto>(url);
  },

  // operation preferences
  getOperationPreferences: async (
    operationName: string
  ): Promise<AxiosResponse<OperationPreferencesDto>> =>
    await api.get(`/operation/${operationName}/preferences`),

  operationPreferencesAverages: async (operationName: string) =>
    await api.get(
      `/operation/${operationName}/production/averages?yearFrom=${
        new Date().getFullYear() - 5
      }&yearTo=${new Date().getFullYear()}`
    ),

  postOperationPreferences: async (operationName: string, data: OperationPreferencesDto) =>
    await api.put(`/operation/${operationName}/preferences`, data, {
      headers: {
        'x-api-version': '2.0'
      }
    }),

  getOperationPriceReport: async (operationName: string) =>
    await api.get(`/operation/${operationName}/preferences/reports`),

  putOperationPriceReport: async (operationName: string, data: OperationPriceReportPayload) =>
    await api.put(`/operation/${operationName}/preferences/reports`, data),

  deleteOperationPriceReport: async (operationName: string, id: number) =>
    await api.delete(`/operation/${operationName}/preferences/reports?id=${id}`),

  // Education
  getEducationVideos: async (agencyName: string): Promise<AxiosResponse<EducationVideo[]>> =>
    await api.get(`/education/video/agency/${agencyName}`),

  getEducationVideoUrl: async (guid: string): Promise<AxiosResponse<EducationVideo>> =>
    await api.get(`/education/video/${guid}`),

  getRecommendedEducationVideos: async (guid: string): Promise<AxiosResponse<EducationVideo[]>> =>
    await api.get(`/education/video/recommended?guid=${guid}`),

  uploadEducationVideo: async (
    fileName: string,
    data: CreateEducationVideoData
  ): Promise<AxiosResponse<{ guid: string; uploadUri: string }>> =>
    await api.post(`/education/video/upload/${fileName}/uri`, data),

  completeUploadEducationVideo: async (guid: string) =>
    await api.put(`/education/video/${guid}/upload/complete`),

  uploadEducationVideoAttachment: async (
    videoGuid: string,
    fileName: string
  ): Promise<AxiosResponse<{ guid: string; uploadUri: string }>> =>
    await api.post(`/education/video/${videoGuid}/attachment/upload/${fileName}/uri`),

  completeUploadEducationVideoAttachment: async (guid: string) =>
    await api.put(`/education/video/attachment/${guid}/upload/complete`),

  uploadEducationVideoThumbnail: async (
    videoGuid: string,
    fileName: string
  ): Promise<AxiosResponse<{ guid: string; uploadUri: string }>> =>
    await api.post(`/education/video/${videoGuid}/thumbnail/upload/${fileName}/uri`),

  completeUploadEducationVideoThumbnail: async (guid: string) =>
    await api.put(`/education/video/thumbnail/${guid}/upload/complete`),

  editEducationVideo: async (data: EducationVideo) => await api.put('/education/video', data),

  editEducationVideos: async (data: EducationVideo[]) =>
    await api.put('/education/video/list', data),

  deleteEducationVideo: async (guid: string) => await api.delete(`/education/video/${guid}`),

  deleteEducationVideoAttachment: async (guid: string) =>
    await api.delete(`/education/video/attachment/${guid}`),

  deleteEducationVideoThumbnail: async (guid: string) =>
    await api.delete(`/education/video/thumbnail/${guid}`),

  getEducationVideoAttachments: async (guid: string) =>
    await api.get(`/education/video/${guid}/attachments`),

  getEducationVideoThumbnail: async (guid: string) =>
    await api.get(`education/video/${guid}/thumbnail`),

  getEducationVideoAttachmentData: async (url: string) =>
    await axios.get(url, { responseType: 'blob' }),

  sendNewsletterToUser: async (data: AccountPreferencesSendReport) =>
    await DRPPriceReportAPI.post(
      `newsletter/price/${formatDateISO(new Date())}/notify/${data.userEmail}`,
      {
        type: data.type ? data.type : 'DrpPriceReport'
      }
    ),

  // operationProducer
  getOperationProducer: async (
    operationName: string,
    includeBindingRequirements = false,
    producerType?: ProducerType
  ) =>
    await api.get(`/operation/${operationName}/producer`, {
      params: {
        includeBindingRequirements: includeBindingRequirements || undefined,
        producerType
      }
    }),

  getProjectedAnalysis: async (operationName: string, year: number, data: ProjectionDataQuery) => {
    let query = '';
    type QueryType =
      | 'projectionModel'
      | 'q1Basis'
      | 'q2Basis'
      | 'q3Basis'
      | 'q4Basis'
      | 'q1AdjustedBasisCwt'
      | 'q2AdjustedBasisCwt'
      | 'q3AdjustedBasisCwt'
      | 'q4AdjustedBasisCwt';
    (
      [
        'projectionModel',
        'q1Basis',
        'q2Basis',
        'q3Basis',
        'q4Basis',
        'q1AdjustedBasisCwt',
        'q2AdjustedBasisCwt',
        'q3AdjustedBasisCwt',
        'q4AdjustedBasisCwt'
      ] as QueryType[]
    ).forEach(q => {
      const value = data[q];
      if (value !== undefined && value !== null) query = addQueryParameter(query, q, value);
    });
    const baseUrl = `/drp/analysis/projected/revenue/${operationName}/${year}`;
    const url = query ? `${baseUrl}?${query}` : baseUrl;
    return await api.get<ProjectionAnalysisDataDto>(url);
  },

  getProjectedAnalysisSettings: async (operationName: string, year: number) =>
    await api.get(`/drp/analysis/projected/revenue/${operationName}/${year}/parameters`),

  updateProjectedAnalysisSettings: async (
    operationName: string,
    data: ProjectionAnalysisParametersDto
  ) =>
    await api.put(`/drp/analysis/projected/revenue/${operationName}/${data.year}/parameters`, data),

  getPriceReports: async (calendarDate: Date, operationName: string) =>
    await api.get(`/drp/analysis/historical/price/${formatDateISO(calendarDate)}/${operationName}`),

  getCMEPriceReports: async (calendarDate: Date) =>
    await api.get(`/drp/analysis/historical/price/cme/${formatDateISO(calendarDate)}`),

  // Scenario What If Endorsements Endpoints

  getScenarioEndorsementSummary: async (operationName: string, scenarioName: string) =>
    await api.get(`/drp/operation/${operationName}/endorsement/summary/whatif/${scenarioName}`),

  getScenarioEndorsementsByQuarter: async (
    operationName: string,
    scenarioName: string,
    year: number,
    quarter: number
  ) =>
    await api.get(
      `/drp/${operationName}/endorsement/whatif/${scenarioName}/calendar/${year}/${quarter}`
    ),

  getScenarioEndorsement: async (operationName: string, scenarioName: string, id: number) =>
    await api.get(`drp/${operationName}/endorsement/whatif/${scenarioName}/${id}`),

  createScenarioEndorsement: async (
    operationName: string,
    scenarioName: string,
    data: EndorsementPayload
  ) => await api.post(`drp/operation/${operationName}/endorsement/whatif/${scenarioName}`, data),

  updateScenarioEndorsement: async (
    operationName: string,
    scenarioName: string,
    id: number,
    data: EndorsementPayload
  ) => await api.put(`/drp/${operationName}/endorsement/whatif/${scenarioName}/${id}`, data),

  deleteScenarioEndorsement: async (operationName: string, scenarioName: string, id: number) =>
    await api.delete(`/drp/${operationName}/endorsement/whatif/${scenarioName}/${id}`),

  // Scenario What If Scenario Endpoints

  getWhatIfScenarios: async (operationName: string) =>
    await api.get<string[]>(`/drp/operation/${operationName}/whatif`),

  getWhatIfScenario: async (operationName: string, scenarioName: string) =>
    await api.get(`/drp/operation/${operationName}/whatif/name?name=${scenarioName}`),

  putWhatIfScenario: async (
    operationName: string,
    scenarioName: string,
    data: ScenarioWhatIfInitialPrices
  ) => await api.put(`/drp/operation/${operationName}/whatif?name=${scenarioName}`, data),

  deleteWhatIfScenario: async (operationName: string, scenarioName: string) =>
    await api.delete(`/drp/operation/${operationName}/whatif?name=${scenarioName}`),

  recalculateScenarioEndorsements: async (operationName: string, scenarioName: string) =>
    await api.put(`/drp/${operationName}/endorsement/whatif/${scenarioName}/price`),

  exportScenarioEndorsementIndemnityToExcel: async (
    operationName: string,
    scenarioName: string,
    id: number
  ) =>
    await api.get(`/drp/${operationName}/endorsement/whatif/${scenarioName}/${id}/export`, {
      responseType: 'blob'
    }),

  // LRP

  getQuoterPreferences: async (operationName: string) =>
    await api.get(`/lrp/${operationName}/preferences`),

  getPricePreferences: async (operationName: string) =>
    await api.get(`/lrp/${operationName}/preferences/reports`),

  updateQuoterPreference: async (
    operationName: string,
    commodity: string,
    coveragePercentageRank: number,
    commodityType: string,
    numberOfHead: number,
    targetWeight: number,
    insuredSharedPercent: number,
    conservationCompliance: number,
    isBeginner: boolean,
    monthSelection: string,
    basisSelection: string,
    strikeSelection: string,
    showAllEndorsementLengths: boolean,
    showCMEPutComparison: boolean,
    showSubsidyOverview: boolean,
    lrpPriceDisplayTypes: string[],
    cmePriceDisplayCommodities: string[] | null = null,
    bfrVfrPercent?: number
  ) => {
    const body = {
      commodity,
      commodityType,
      coveragePercentageRank,
      numberOfHead,
      targetWeight,
      insuredSharedPercent,
      conservationCompliance,
      isBeginner,
      bfrVfrPercent,
      monthSelection,
      basisSelection,
      strikeSelection,
      showAllEndorsementLengths,
      showCMEPutComparison,
      showSubsidyOverview,
      lrpPriceDisplayTypes,
      cmePriceDisplayCommodities
    };

    return await api.post(`/lrp/${operationName}/preferences`, body, {
      headers: {
        'x-api-version': '2.0'
      }
    });
  },

  createPricePreference: async (operationName: string, data: PricePreferencePayload) =>
    await api.post(`/lrp/${operationName}/preferences/reports`, data),

  deletePricePreference: async (operationName: string, id: number) =>
    await api.delete(`/lrp/${operationName}/preferences/reports/${id}`),

  getFilter: async (salesEffectiveDate?: Date) => {
    const salesDate = formatDateISO(salesEffectiveDate);

    let query = '';
    query = salesEffectiveDate ? addQueryParameter(query, 'salesEffectiveDate', salesDate) : query;

    const baseUrl = '/lrp/filters';
    const url = query ? `${baseUrl}?${query}` : baseUrl;
    return await api.get(url);
  },

  requestCoverage: async (operationName: string, data: LrpCoverageRequestModel) => {
    const salesDate = formatDateISO(data.salesEffectiveDate);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- refactor later
    const newDefinitions: any = [];
    data.definitions.forEach(element => {
      const newCalculationParameters = {
        ...element.calculationParameters,
        salesEffectiveDate: salesDate
      };
      newDefinitions.push({
        ...element,
        calculationParameters: newCalculationParameters
      });
    });
    const newData = {
      producerUserName: data.producerUserName,
      agentUserName: data.agentUserName,
      signatureText: data.signatureText,
      salesEffectiveDate: salesDate,
      definitions: newDefinitions,
      ignoreDuplicateEndorsements: data.ignoreDuplicateEndorsements,
      ignoreDuplicateRequests: data.ignoreDuplicateRequests
    };

    await api.post(`/lrp/${operationName}/endorsement/request`, newData);
  },

  lrpCoverageQuery: (params: LrpCalculationParameters, isHistorical = false): string => {
    const salesDate = formatDateISO(params.salesEffectiveDate);

    let query = '';
    query = params.commodity ? addQueryParameter(query, 'Commodity', params.commodity) : query;
    query = params.coveragePercentageRank
      ? addQueryParameter(query, 'CoveragePercentageRank', params.coveragePercentageRank)
      : query;
    query = params.typeCode ? addQueryParameter(query, 'TypeCode', params.typeCode) : query;
    query = params.salesEffectiveDate
      ? addQueryParameter(query, 'SalesEffectiveDate', salesDate)
      : query;
    query = params.endorsementLength
      ? addQueryParameter(query, 'EndorsementLength', params.endorsementLength)
      : query;
    query = params.numberOfHead
      ? addQueryParameter(query, 'NumberOfHead', params.numberOfHead)
      : query;
    query = params.targetWeight
      ? addQueryParameter(query, 'TargetWeight', params.targetWeight)
      : query;
    query = params.insuredSharePercent
      ? addQueryParameter(query, 'InsuredSharePercent', params.insuredSharePercent)
      : query;
    query =
      params.conservationCompliance !== undefined
        ? addQueryParameter(query, 'ConservationCompliance', params.conservationCompliance)
        : query;
    query = params.isBeginner
      ? addQueryParameter(query, 'IsBeginner', 'true')
      : addQueryParameter(query, 'IsBeginner', 'false');
    query =
      params.isBeginner && params.bfrVfrPercent != null
        ? addQueryParameter(query, 'BfrVfrPercent', params.bfrVfrPercent)
        : query;
    query = params.putMonthSelection
      ? addQueryParameter(query, 'PutMonthSelection', params.putMonthSelection)
      : query;
    query = params.putBasisSelection
      ? addQueryParameter(query, 'PutBasisSelection', params.putBasisSelection)
      : query;
    query = params.putStrikeSelection
      ? addQueryParameter(query, 'PutStrikeSelection', params.putStrikeSelection)
      : query;
    query = isHistorical ? addQueryParameter(query, 'historic', 'true') : query;

    return query;
  },

  getCoverageTable: async (params: LrpCalculationParameters) => {
    const query = insuranceAPI.lrpCoverageQuery(params);
    const url = query ? `/lrp/coverage?${query}` : '/lrp/coverage';
    return await api.get(url);
  },

  getCoverageTableAllEndorsementLengths: async (params: LrpCalculationParameters) => {
    const query = insuranceAPI.lrpCoverageQuery(params);
    const url = query ? `/lrp/coverage/period?${query}` : '/lrp/coverage/period';
    return await api.get(url);
  },

  getCoverageTableHistorical: async (params: LrpCalculationParameters, isHistorical = false) => {
    const query = insuranceAPI.lrpCoverageQuery(params, isHistorical);
    const url = query ? `/lrp/coverage/historic?${query}` : '/lrp/coverage/historic';
    return await api.get(url);
  },

  // Sales Statistics
  getDrpSalesStatistics: async (
    calendarYearFrom?: number,
    calendarYearTo?: number,
    quarterFrom?: number,
    quarterTo?: number,
    reinsuranceYearFrom?: number,
    reinsuranceYearTo?: number,
    salesEffectiveDateFrom?: Date,
    salesEffectiveDateTo?: Date,
    stateCode?: string,
    countyCodes?: string[]
  ) => {
    let query = '';
    query = calendarYearFrom
      ? addQueryParameter(query, 'calendarYearFrom', calendarYearFrom)
      : query;
    query = calendarYearTo ? addQueryParameter(query, 'calendarYearTo', calendarYearTo) : query;
    query = quarterFrom ? addQueryParameter(query, 'quarterFrom', quarterFrom) : query;
    query = quarterTo ? addQueryParameter(query, 'quarterTo', quarterTo) : query;
    query = reinsuranceYearFrom
      ? addQueryParameter(query, 'reinsuranceYearFrom', reinsuranceYearFrom)
      : query;
    query = reinsuranceYearTo
      ? addQueryParameter(query, 'reinsuranceYearTo', reinsuranceYearTo)
      : query;
    query = salesEffectiveDateFrom
      ? addQueryParameter(query, 'salesEffectiveDateFrom', formatDateISO(salesEffectiveDateFrom))
      : query;
    query = salesEffectiveDateTo
      ? addQueryParameter(query, 'salesEffectiveDateTo', formatDateISO(salesEffectiveDateTo))
      : query;
    query = stateCode ? addQueryParameter(query, 'stateCode', stateCode) : query;
    const countyCodesString = countyCodes?.map(e => `CountyCodes=${e}`);

    const baseUrl = '/statistics';
    const parts = [];
    if (query) parts.push(query);
    if (countyCodesString) parts.push(countyCodesString.join('&'));
    const url = parts.length > 0 ? `${baseUrl}?${parts.join('&')}` : baseUrl;

    return await api.get(url);
  },

  getDrpSalesStatisticsBar: async (
    calendarYearFrom?: number,
    calendarYearTo?: number,
    quarterFrom?: number,
    quarterTo?: number,
    reinsuranceYearFrom?: number,
    reinsuranceYearTo?: number,
    salesEffectiveDateFrom?: Date,
    salesEffectiveDateTo?: Date,
    stateCode?: string,
    countyCodes?: string[]
  ) => {
    let query = '';
    query = calendarYearFrom
      ? addQueryParameter(query, 'calendarYearFrom', calendarYearFrom)
      : query;
    query = calendarYearTo ? addQueryParameter(query, 'calendarYearTo', calendarYearTo) : query;
    query = quarterFrom ? addQueryParameter(query, 'quarterFrom', quarterFrom) : query;
    query = quarterTo ? addQueryParameter(query, 'quarterTo', quarterTo) : query;
    query = reinsuranceYearFrom
      ? addQueryParameter(query, 'reinsuranceYearFrom', reinsuranceYearFrom)
      : query;
    query = reinsuranceYearTo
      ? addQueryParameter(query, 'reinsuranceYearTo', reinsuranceYearTo)
      : query;
    query = salesEffectiveDateFrom
      ? addQueryParameter(query, 'salesEffectiveDateFrom', formatDateISO(salesEffectiveDateFrom))
      : query;
    query = salesEffectiveDateTo
      ? addQueryParameter(query, 'salesEffectiveDateTo', formatDateISO(salesEffectiveDateTo))
      : query;
    query = stateCode ? addQueryParameter(query, 'stateCode', stateCode) : query;
    const countyCodesString = countyCodes?.map(e => `CountyCodes=${e}`);

    const baseUrl = '/statistics/bar';
    const parts = [];
    if (query) parts.push(query);
    if (countyCodesString) parts.push(countyCodesString.join('&'));
    const url = parts.length > 0 ? `${baseUrl}?${parts.join('&')}` : baseUrl;

    return await api.get(url);
  },

  getDrpSalesStatisticsHedgeRatio: async (
    operationName: string,
    year?: number,
    quarter?: number,
    stateCodes?: string[]
  ) => {
    let query = '';
    query = year ? addQueryParameter(query, 'year', year) : query;
    query = quarter ? addQueryParameter(query, 'quarter', quarter) : query;
    const stateCodesString = stateCodes?.map(e => `stateCodes=${e}`);

    const baseUrl = `/statistics/hedge/${operationName}`;
    const parts = [];
    if (query) parts.push(query);
    if (stateCodesString) parts.push(stateCodesString.join('&'));
    const url = parts.length > 0 ? `${baseUrl}?${parts.join('&')}` : baseUrl;

    return await api.get(url);
  },

  getDrpSalesStatisticsGroup: async (
    groupOptions?: string[],
    calendarYearFrom?: number,
    calendarYearTo?: number,
    quarterFrom?: number,
    quarterTo?: number,
    reinsuranceYearFrom?: number,
    reinsuranceYearTo?: number,
    salesEffectiveDateFrom?: Date,
    salesEffectiveDateTo?: Date,
    stateCode?: string,
    countyCodes?: string[]
  ) => {
    let query = '';
    const groupOptionsString = groupOptions?.map(e => `GroupOptions=${e}`);
    query = calendarYearFrom
      ? addQueryParameter(query, 'CalendarYearFrom', calendarYearFrom)
      : query;
    query = calendarYearTo ? addQueryParameter(query, 'CalendarYearTo', calendarYearTo) : query;
    query = quarterFrom ? addQueryParameter(query, 'quarterFrom', quarterFrom) : query;
    query = quarterTo ? addQueryParameter(query, 'quarterTo', quarterTo) : query;
    query = reinsuranceYearFrom
      ? addQueryParameter(query, 'reinsuranceYearFrom', reinsuranceYearFrom)
      : query;
    query = reinsuranceYearTo
      ? addQueryParameter(query, 'reinsuranceYearTo', reinsuranceYearTo)
      : query;
    query = salesEffectiveDateFrom
      ? addQueryParameter(query, 'salesEffectiveDateFrom', formatDateISO(salesEffectiveDateFrom))
      : query;
    query = salesEffectiveDateTo
      ? addQueryParameter(query, 'salesEffectiveDateTo', formatDateISO(salesEffectiveDateTo))
      : query;
    query = stateCode ? addQueryParameter(query, 'stateCode', stateCode) : query;
    const countyCodesString = countyCodes?.map(e => `CountyCodes=${e}`);

    const baseUrl = '/statistics/table';
    const parts = [];
    if (groupOptionsString) parts.push(groupOptionsString.join('&'));
    if (query) parts.push(query);
    if (countyCodesString) parts.push(countyCodesString.join('&'));
    const url = parts.length > 0 ? `${baseUrl}?${parts.join('&')}` : baseUrl;

    return await api.get(url);
  },

  exportGroupTableToExcel: async (groupTableData: GroupStatisticsAllExcel) =>
    await api.post('/statistics/export/excel', groupTableData, { responseType: 'blob' }),

  // Scenario What If Operation Data Endpoints

  getScenarioWhatIfData: async (
    operationName: string,
    scenarioName: string
  ): Promise<AxiosResponse<HistoricalDataDto[]>> =>
    await api.get(`/operation/${operationName}/production/whatif/${scenarioName}`),

  editScenarioWhatIfDataRecord: async (
    operationName: string,
    scenarioName: string,
    data: HistoricalDataEssentials
  ) => await api.put(`/operation/${operationName}/production/whatif/${scenarioName}`, [data]),

  deleteScenarioWhatIfDataRecord: async (
    operationName: string,
    scenarioName: string,
    year: number,
    month: number
  ) =>
    await api.delete(
      `/operation/${operationName}/production/whatif/${scenarioName}/${year}/${month}`
    ),

  saveAllScenarioWhatIfDataRecords: async (
    operationName: string,
    scenarioName: string,
    data: HistoricalDataEssentials[]
  ) => await api.put(`/operation/${operationName}/production/whatif/${scenarioName}`, data),

  downloadScenarioWhatIfData: async (operationName: string, scenarioName: string) =>
    await api.get(`/operation/${operationName}/production/whatif/${scenarioName}/download`),

  resetWhatIf: async (
    operationName: string,
    scenarioName: string,
    resetEndorsements: boolean,
    resetPrices: boolean,
    resetOperationData: boolean
  ) =>
    await api.put(`/drp/operation/${operationName}/whatif/reset`, {
      scenarioName,
      endorsements: resetEndorsements,
      prices: resetPrices,
      operationData: resetOperationData
    }),
  updateScenarioAnalysisWhatIf: async (data: ScenarioAnalysisWhatIfPost) =>
    await api.post('/whatif', data),

  getScenarioAnalysisProjectedRevenue: async (
    operationName: string,
    year: number,
    scenarioName: string,
    data: ProjectionDataQuery
  ) => {
    let query = '';
    type QueryType =
      | 'projectionModel'
      | 'q1Basis'
      | 'q2Basis'
      | 'q3Basis'
      | 'q4Basis'
      | 'q1AdjustedBasisCwt'
      | 'q2AdjustedBasisCwt'
      | 'q3AdjustedBasisCwt'
      | 'q4AdjustedBasisCwt';
    (
      [
        'projectionModel',
        'q1Basis',
        'q2Basis',
        'q3Basis',
        'q4Basis',
        'q1AdjustedBasisCwt',
        'q2AdjustedBasisCwt',
        'q3AdjustedBasisCwt',
        'q4AdjustedBasisCwt'
      ] as QueryType[]
    ).forEach(q => {
      const value = data[q];
      if (value !== undefined && value !== null) query = addQueryParameter(query, q, value);
    });
    return await api.get<ProjectionAnalysisDataDto>(
      `drp/analysis/projected/revenue/${operationName}/${year}/whatif/${scenarioName}${
        query ? `?${query}` : ''
      }`
    );
  },

  getOperationReports: async (
    agencyName: string,
    calendarYearFrom?: number,
    calendarYearTo?: number,
    quarterFrom?: number,
    quarterTo?: number,
    reinsuranceYearFrom?: number,
    reinsuranceYearTo?: number
  ) => {
    let query = '';
    query = calendarYearFrom
      ? addQueryParameter(query, 'calendarYearFrom', calendarYearFrom)
      : query;
    query = calendarYearTo ? addQueryParameter(query, 'calendarYearTo', calendarYearTo) : query;
    query = quarterFrom ? addQueryParameter(query, 'quarterFrom', quarterFrom) : query;
    query = quarterTo ? addQueryParameter(query, 'quarterTo', quarterTo) : query;
    query = reinsuranceYearFrom
      ? addQueryParameter(query, 'reinsuranceYearFrom', reinsuranceYearFrom)
      : query;
    query = reinsuranceYearTo
      ? addQueryParameter(query, 'reinsuranceYearTo', reinsuranceYearTo)
      : query;

    const baseUrl = `/management/${agencyName}/report/drp/operation`;
    const url = query ? `${baseUrl}?${query}` : baseUrl;
    return await api.get(url);
  },

  getSmsUsage: async (
    startDate?: Date,
    endDate?: Date,
    _agency?: string,
    groupByMonth?: boolean
  ) => {
    let query = '';
    query = startDate ? addQueryParameter(query, 'DateFrom', formatDateISO(startDate)) : query;
    query = endDate ? addQueryParameter(query, 'DateTo', formatDateISO(endDate)) : query;
    query =
      groupByMonth !== undefined ? addQueryParameter(query, 'groupByMonth', groupByMonth) : query;

    const url = query ? `/sms/usage?${query}` : '/sms/usage';
    return await api.get(url);
  },

  getSmsUsageForAgency: async (
    agencyName: string,
    startDate?: Date,
    endDate?: Date,
    _agency?: string,
    groupByMonth?: boolean
  ) => {
    let query = '';
    query = startDate ? addQueryParameter(query, 'DateFrom', formatDateISO(startDate)) : query;
    query = endDate ? addQueryParameter(query, 'DateTo', formatDateISO(endDate)) : query;
    query =
      groupByMonth !== undefined ? addQueryParameter(query, 'groupByMonth', groupByMonth) : query;

    const baseUrl = `/sms/${agencyName}/usage`;
    const url = query ? `${baseUrl}?${query}` : baseUrl;
    return await api.get(url);
  },

  downloadSmsUsageTable: async (startDate?: Date, endDate?: Date, agencyName?: string) => {
    let query = '';
    query = startDate ? addQueryParameter(query, 'DateFrom', formatDateISO(startDate)) : query;
    query = endDate ? addQueryParameter(query, 'DateTo', formatDateISO(endDate)) : query;
    query = agencyName ? addQueryParameter(query, 'AgencyName', agencyName) : query;

    const url = query ? `/sms/excel?${query}` : '/sms/excel';
    return await api.get(url, { responseType: 'blob' });
  },

  getLrpEndorsements: async (operationName: string, data: LrpEndorsementSummaryDto) => {
    let query = '';

    query = data.commodity ? addQueryParameter(query, 'Commodity', data.commodity) : query;
    query = data.typeCode ? addQueryParameter(query, 'TypeCode', data.typeCode) : query;
    query = data.reinsuranceYearFrom
      ? addQueryParameter(query, 'ReinsuranceYearFrom', data.reinsuranceYearFrom)
      : query;
    query = data.reinsuranceYearTo
      ? addQueryParameter(query, 'ReinsuranceYearTo', data.reinsuranceYearTo)
      : query;
    query = data.calendarYearFrom
      ? addQueryParameter(query, 'CalendarYearFrom', data.calendarYearFrom)
      : query;
    query = data.calendarYearTo
      ? addQueryParameter(query, 'CalendarYearTo', data.calendarYearTo)
      : query;
    query = data.salesEffectiveDateFrom
      ? addQueryParameter(
          query,
          'SalesEffectiveDateFrom',
          formatDateISO(data.salesEffectiveDateFrom)
        )
      : query;
    query = data.salesEffectiveDateTo
      ? addQueryParameter(query, 'SalesEffectiveDateTo', formatDateISO(data.salesEffectiveDateTo))
      : query;
    query = data.endDateFrom
      ? addQueryParameter(query, 'EndDateFrom', formatDateISO(data.endDateFrom))
      : query;
    query = data.endDateTo
      ? addQueryParameter(query, 'EndDateTo', formatDateISO(data.endDateTo))
      : query;
    query = data.topCondition ? addQueryParameter(query, 'TopCondition', data.topCondition) : query;
    query = data.groupByEndMonth
      ? addQueryParameter(query, 'GroupByEndMonth', data.groupByEndMonth)
      : query;
    query = addQueryParameterArray(query, 'TagNames', data.tagNames ?? []);

    if (data?.actualPriceDate) {
      query = addQueryParameter(query, 'actualPriceDate', data?.actualPriceDate);
    }
    const baseUrl = `/lrp/${operationName}/endorsement`;
    const url = query ? `${baseUrl}?${query}` : baseUrl;
    return await api.get(url);
  },

  getLrpEndorsementFilter: async (operationName: string) =>
    await api.get(`/lrp/${operationName}/endorsement/filter`),

  getLrpEndorsementSummaryTable: async (operationName: string, data: LrpEndorsementSummaryDto) => {
    let query = '';
    query = data.commodity ? addQueryParameter(query, 'commodity', data.commodity) : query;
    query = data.typeCode ? addQueryParameter(query, 'typeCode', data.typeCode) : query;
    query = data.reinsuranceYearFrom
      ? addQueryParameter(query, 'reinsuranceYearFrom', data.reinsuranceYearFrom)
      : query;
    query = data.reinsuranceYearTo
      ? addQueryParameter(query, 'reinsuranceYearTo', data.reinsuranceYearTo)
      : query;
    query = data.calendarYearFrom
      ? addQueryParameter(query, 'calendarYearFrom', data.calendarYearFrom)
      : query;
    query = data.calendarYearTo
      ? addQueryParameter(query, 'calendarYearTo', data.calendarYearTo)
      : query;
    query = data.salesEffectiveDateFrom
      ? addQueryParameter(
          query,
          'salesEffectiveDateFrom',
          formatDateISO(data.salesEffectiveDateFrom)
        )
      : query;
    query = data.salesEffectiveDateTo
      ? addQueryParameter(query, 'salesEffectiveDateTo', formatDateISO(data.salesEffectiveDateTo))
      : query;
    query = data.endDateFrom
      ? addQueryParameter(query, 'endDateFrom', formatDateISO(data.endDateFrom))
      : query;
    query = data.endDateTo
      ? addQueryParameter(query, 'endDateTo', formatDateISO(data.endDateTo))
      : query;
    query = data.topCondition ? addQueryParameter(query, 'topCondition', data.topCondition) : query;
    query = data.groupByEndMonth
      ? addQueryParameter(query, 'groupByEndMonth', data.groupByEndMonth)
      : query;
    query = addQueryParameterArray(query, 'TagNames', data.tagNames ?? []);
    if (data?.actualPriceDate) {
      query = addQueryParameter(query, 'actualPriceDate', data?.actualPriceDate);
    }
    const baseUrl = `/lrp/${operationName}/endorsement/summary`;
    const url = query ? `${baseUrl}?${query}` : baseUrl;
    return await api.get(url);
  },

  recalculateLrpEndorsements: async (operationName: string) =>
    await api.put(`/lrp/${operationName}/endorsement/price`),

  createLrpEndorsement: async (operationName: string, data: LrpEndorsementDefinition) => {
    const newData = {
      ...data,
      calculationParameters: {
        ...data.calculationParameters,
        salesEffectiveDate: formatDateISO(data.calculationParameters.salesEffectiveDate)
      }
    };
    return await api.post(`/lrp/${operationName}/endorsement`, newData);
  },

  deleteLrpEndorsement: async (data: LrpDeleteEndorsement) =>
    await api.delete(`/lrp/${data.operationName}/endorsement/${data.id}`),

  updateLrpEndorsement: async (operationName: string, data: LrpUpdateEndorsement) => {
    const newData = {
      ...data,
      calculationParameters: {
        ...data.calculationParameters,
        salesEffectiveDate: formatDateISO(data.calculationParameters.salesEffectiveDate)
      }
    };
    return await api.put(`/lrp/${operationName}/endorsement/${data.id}`, newData);
  },

  getLrpAnalysisCurrentPrices: async (data: LrpAnalysisFilterSettings) => {
    let query = '';
    query = data.salesEffectiveDate
      ? addQueryParameter(query, 'Date', formatDateISO(data.salesEffectiveDate))
      : query;
    query = data.filterComponent.commodity
      ? addQueryParameter(query, 'Commodity', data.filterComponent.commodity.id)
      : query;
    query = data.filterComponent.commodityType
      ? addQueryParameter(query, 'CommodityType', data.filterComponent.commodityType.id)
      : query;
    query = data.filterComponent.deductible
      ? addQueryParameter(query, 'Deductible', data.filterComponent.deductible.id)
      : query;
    query = data.filterComponent.numberOfHead
      ? addQueryParameter(query, 'NumberOfHead', data.filterComponent.numberOfHead)
      : query;
    query = data.filterComponent.targetWeight
      ? addQueryParameter(query, 'TargetWeight', data.filterComponent.targetWeight)
      : query;
    query = data.yearRange.yearTo
      ? addQueryParameter(query, 'YearTo', data.yearRange.yearTo)
      : query;
    query = data.yearRange.yearFrom
      ? addQueryParameter(query, 'YearFrom', data.yearRange.yearFrom)
      : query;

    const url1 = query ? `/lrp/analysis/monthly?${query}` : '/lrp/analysis/monthly';
    return await api.get(url1);
  },

  getLrpAnalysisHistoricalPrices: async (data: LrpAnalysisFilterSettings) => {
    let query = '';
    query = data.salesEffectiveDate
      ? addQueryParameter(query, 'Date', formatDateISO(data.salesEffectiveDate))
      : query;
    query = data.filterComponent.commodity
      ? addQueryParameter(query, 'Commodity', data.filterComponent.commodity.id)
      : query;
    query = data.filterComponent.commodityType
      ? addQueryParameter(query, 'CommodityType', data.filterComponent.commodityType.id)
      : query;
    query = data.filterComponent.deductible
      ? addQueryParameter(query, 'Deductible', data.filterComponent.deductible.id)
      : query;
    query = data.filterComponent.numberOfHead
      ? addQueryParameter(query, 'NumberOfHead', data.filterComponent.numberOfHead)
      : query;
    query = data.filterComponent.targetWeight
      ? addQueryParameter(query, 'TargetWeight', data.filterComponent.targetWeight)
      : query;
    query = data.yearRange.yearTo
      ? addQueryParameter(query, 'YearTo', data.yearRange.yearTo)
      : query;
    query = data.yearRange.yearFrom
      ? addQueryParameter(query, 'YearFrom', data.yearRange.yearFrom)
      : query;

    const url = query ? `/lrp/analysis/yearly?${query}` : '/lrp/analysis/yearly';
    return await api.get(url);
  },
  getLrpPriceEvolutionAnalysis: async (data: LrpPriceEvolutionAnalysisSettings) => {
    let query = '';
    query =
      data.endMonth && data.year
        ? addQueryParameter(query, 'Date', `${data.year}-${data.endMonth.id}-15`)
        : query;
    query = data.filterComponent.commodity
      ? addQueryParameter(query, 'Commodity', data.filterComponent.commodity.id)
      : query;
    query = data.filterComponent.commodityType
      ? addQueryParameter(query, 'CommodityType', data.filterComponent.commodityType.id)
      : query;
    query = data.filterComponent.deductible
      ? addQueryParameter(query, 'Deductible', data.filterComponent.deductible.id)
      : query;
    query = data.filterComponent.numberOfHead
      ? addQueryParameter(query, 'NumberOfHead', data.filterComponent.numberOfHead)
      : query;
    query = data.filterComponent.targetWeight
      ? addQueryParameter(query, 'TargetWeight', data.filterComponent.targetWeight)
      : query;
    query = data.yearRange.yearTo
      ? addQueryParameter(query, 'YearTo', data.yearRange.yearTo)
      : query;
    query = data.yearRange.yearFrom
      ? addQueryParameter(query, 'YearFrom', data.yearRange.yearFrom)
      : query;

    const baseUrl = `/lrp/analysis/historic/${data.measure.id}`;
    const url = query ? `${baseUrl}?${query}` : baseUrl;
    return await api.get(url);
  },

  downloadManagementReportTable: async (agencyName: string, payload: OperationReport[]) =>
    await api.post(`/management/${agencyName}/report/drp/operation/export/excel`, payload, {
      responseType: 'blob'
    }),

  getTags: async (operationName: string) => await api.get(`/operation/${operationName}/tag`),

  createTag: async (operationName: string, requestBody: TagRequestBody) =>
    await api.post(`/operation/${operationName}/tag`, requestBody),

  editTag: async (operationName: string, requestBody: TagRequestBody, tagName: string) =>
    await api.put(`/operation/${operationName}/tag/${tagName}`, requestBody),

  deleteTag: async (operationName: string, tagName: string) =>
    await api.delete(`/operation/${operationName}/tag/${tagName}`),

  mergeTags: async (operationName: string, tagName: string, selectedTags: string[]) =>
    await api.put(`/operation/${operationName}/tag/${tagName}/tags`, selectedTags),

  getTagTypes: async (agencyName: string) => await api.get(`agency/${agencyName}/tagType`),

  createTagType: async (agencyName: string, tagTypeRequestBody: TagTypeRequestBody) =>
    await api.post(`/agency/${agencyName}/tagType`, tagTypeRequestBody),

  editTagType: async (
    agencyName: string,
    tagTypeRequestBody: TagTypeRequestBody,
    tagType: string
  ) => await api.put(`/agency/${agencyName}/tagType/${tagType}`, tagTypeRequestBody),

  deleteTagType: async (agencyName: string, tagType: string) =>
    await api.delete(`/agency/${agencyName}/tagType/${tagType}`),

  getUnsubscribed: async (
    unsubscribeToken: string,
    endpoint: string,
    queryParamName: string,
    queryParamValue: string
  ) => {
    if (queryParamName) {
      return await api.get(
        `${endpoint}?unsubscribeToken=${unsubscribeToken}&${queryParamName}=${queryParamValue}`
      );
    }

    return await api.get(`${endpoint}?unsubscribeToken=${unsubscribeToken}`);
  }
};

export default insuranceAPI;
